/**
 * EmployeePendingProposals
 *
 * Anzeige der offenen Weiterbildungs-/Qualifikations-Anträge eines Mitarbeiters
 * aus Sicht der Vorgesetzten. Wird im EmployeeDetail eingeblendet.
 *
 * - Lädt `/qualification-proposals/employee/{id}?status=pending`
 * - Bei 403 (User ist nicht zuständig) wird die Karte stillschweigend ausgeblendet
 * - Erlaubt direktes Freigeben/Ablehnen oder springt zur Inbox für Inline-Edit
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Hourglass, CheckCircle2, XCircle, Pencil, GraduationCap, FileText, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { qualificationProposalsAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return iso; }
};

export const EmployeePendingProposals = ({ employeeId, employeeName }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [working, setWorking] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await qualificationProposalsAPI.forEmployee(employeeId, 'pending');
      setItems(res.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setUnauthorized(true);
      } else {
        // andere Fehler still ignorieren – Karte gar nicht erst zeigen
        setUnauthorized(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (employeeId) load(); }, [employeeId]); // eslint-disable-line

  const approve = async (p) => {
    if (!window.confirm(`Antrag „${p.payload?.name || p.payload?.title || 'Eintrag'}" freigeben?`)) return;
    setWorking(true);
    try {
      await qualificationProposalsAPI.approve(p.id);
      toast.success('Antrag freigegeben');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Freigabe fehlgeschlagen');
    } finally { setWorking(false); }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error('Bitte Grund angeben'); return; }
    setWorking(true);
    try {
      await qualificationProposalsAPI.reject(rejectFor.id, rejectReason.trim());
      toast.success('Antrag abgelehnt');
      setRejectFor(null);
      setRejectReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ablehnen fehlgeschlagen');
    } finally { setWorking(false); }
  };

  // Versteckt, wenn User keine Berechtigung hat ODER keine offenen Anträge da sind
  if (unauthorized) return null;
  if (!loading && items.length === 0) return null;

  return (
    <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-950/10" data-testid="employee-pending-proposals">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <Hourglass className="h-4 w-4 text-amber-700" />
          Offene Qualifikations-Anträge
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
            {items.length}
          </Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          {employeeName ? `${employeeName} hat ` : ''}offene Selbst-Anträge, die deine Freigabe brauchen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Lade …
          </div>
        ) : (
          items.map((p) => {
            const data = p.payload || {};
            const name = data.name || data.title || (p.action === 'delete' ? 'Eintrag löschen' : 'Neuer Eintrag');
            const kindLabel = p.kind === 'training' ? 'Weiterbildung' : 'Qualifikation';
            const actionLabel = { create: 'Neu', update: 'Änderung', delete: 'Löschung' }[p.action];

            return (
              <div
                key={p.id}
                className="rounded-md border border-amber-200 bg-white dark:bg-card p-3 space-y-2"
                data-testid={`pending-proposal-row-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <GraduationCap className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="font-medium text-sm truncate">{name}</span>
                      <Badge variant="outline" className="text-[10px]">{kindLabel}</Badge>
                      <Badge variant="outline" className="text-[10px]">{actionLabel}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {data.provider || data.institution || '—'}
                      {(data.completed_date || data.date) && ` · ${formatShortDate(data.completed_date || data.date)}`}
                      {' · eingereicht '}{formatShortDate(p.created_at)}
                    </p>
                    {p.reason && <p className="text-xs italic mt-1">„{p.reason}"</p>}
                    {p.document_filename && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <FileText className="h-3 w-3" /> {p.document_filename}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => navigate('/qualifikations-antraege')}
                    data-testid={`pending-edit-${p.id}`}
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Bearbeiten
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={working}
                    onClick={() => approve(p)}
                    data-testid={`pending-approve-${p.id}`}
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Freigeben
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                    disabled={working}
                    onClick={() => setRejectFor(p)}
                    data-testid={`pending-reject-${p.id}`}
                  >
                    <XCircle className="h-3 w-3 mr-1" /> Ablehnen
                  </Button>
                </div>
              </div>
            );
          })
        )}

        <div className="pt-1">
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => navigate('/qualifikations-antraege')}
          >
            Alle Anträge in der Inbox öffnen <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>

      {/* Ablehnen-Dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(''); } }}>
        <DialogContent className="sm:max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Antrag ablehnen</DialogTitle>
            <DialogDescription>Der Mitarbeiter wird mit deinem Grund benachrichtigt.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Grund der Ablehnung"
            rows={3}
            data-testid="pending-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)} disabled={working}>Abbrechen</Button>
            <Button onClick={submitReject} disabled={working} className="bg-destructive hover:bg-destructive/90" data-testid="pending-reject-confirm">
              {working && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmployeePendingProposals;
